import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { prettyMDY } from '../../../../../shared/utils/dates';
import { stripURL } from '../../../../../shared/utils/url';
import { DetailsPanelLink, DetailsPanelList } from '../../../generic/DetailsPanel';
import PackageLabelsList from '../../../shared/packageLabels/PackageLabelsList';
import type { Snippet } from '../../../../../shared/snippets';

import DetailsSection from './DetailsSection';

type Props = {
  snippet: Snippet;
};

/**
 * The Package Summary drawer details.
 */
const PackageSummary: VoidFunctionComponent<Props> = ({ snippet }) => {
  const rows = useMemo(() => {
    return [
      {
        label: 'Version',
        content: (
          <span>
            {snippet.version}
          </span>
        ),
      },
      {
        label: 'Release Date',
        content: (
          <span>
            {snippet.releaseDate ? prettyMDY(snippet.releaseDate) : <span className="italic">(Unknown)</span>}
          </span>
        ),
      },
      {
        label: 'Homepage',
        content: snippet?.homeUrl ? (
          <DetailsPanelLink to={snippet.homeUrl} title={snippet.homeUrl}>
            {stripURL(snippet.homeUrl)}
          </DetailsPanelLink>
        ) : (
          <span className="italic">
            (No homepage)
          </span>
        ),
      },
      {
        label: 'Source Code',
        content: snippet?.codeUrl ? (
          <DetailsPanelLink to={snippet.codeUrl} title={snippet.codeUrl}>
            {stripURL(snippet.codeUrl)}
          </DetailsPanelLink>
        ) : (
          <span className="italic">
            (No source code)
          </span>
        ),
      },
      {
        label: 'Locator',
        content: (
          <span>
            {snippet.locator ?? <span className="italic">(Unknown)</span>}
          </span>
        ),
      },
      {
        label: 'Labels',
        content: snippet.labels?.length ? (
          <PackageLabelsList
            labels={snippet.labels}
            dependencyVersion={snippet.version}
            labelPrefix=""
          />
        ) : (
          <span className="italic">
            (No labels)
          </span>
        ),
      },
    ];
  }, [snippet]);

  return (
    <DetailsSection heading="Package Summary">
      <DetailsPanelList rows={rows} />
    </DetailsSection>
  );
};

export default PackageSummary;
